import { TiledGameObject, PhaserGeneralGameObjectType } from "src/games/shared/phaserHelpers/HandyTypes";
import { PhaserGamePad } from "src/games/shared/Gamepad";
import bindAllOfThis from "src/utils/BindThisHelper";
import { Zone } from '../objects/Zone';
import { Item } from "../objects/Item";
import { PhaserModalConfig, PhaserModal } from "src/games/shared/Modal";
import { Color } from "src/games/shared/ColorValue";
import TranslateApi from "src/utils/TranslateHelper";
import { PhaserButtonConfig } from "src/games/shared/Button";
import initializeInteractiveCursor from "src/games/shared/InteractiveCursorUtil";
import { Deadzone } from "../objects/Deadzone";
//import { EndGameScene } from "src/games/LandscapingGame/scenes/EndGameScene";
import AudioTranslations from "src/constants/AudioTranslations";

class ScoreObject {
    public id: string;
    public display: string;
    public score: number;
}

export class MainScene extends Phaser.Scene {
    gameWidth: number;
    gameHeight: number;
    player: Phaser.Physics.Arcade.Sprite;
    starDisk: Phaser.GameObjects.Sprite;
    starHauler: Phaser.GameObjects.Sprite;
    currentItem: Item;
    drivingStarDisk: boolean;
    drivingStarHauler: boolean;
    gamePad: PhaserGamePad;
    speed: number = 0;
    background: Phaser.GameObjects.Image;
    map: Phaser.Tilemaps.Tilemap;
    scores: ScoreObject[] = [];
    scoreTiles: Phaser.GameObjects.Text[] = [];
    speechBubble: Phaser.GameObjects.Container;
    allItems: Item[];
    allZones: Zone[];
    carryingItem: boolean;
    helpButton: Phaser.GameObjects.Image;
    helpText: Phaser.GameObjects.GameObject[];
    showingHelpText: boolean;
    deadZones: Deadzone[] = [];
    xVel: number = 0;
    yVel: number = 0;

    private translateString = "Landscaping.Phaser.MainScene.";
    private WALKING_SPEED: number = 100;
    private DRIVING_SPEED: number = 250;

    constructor() {
        super({
            key: "MainScene"
        });
    }

    preload(): void {
        // All preloaded assets will be stored in the texture manager by the given key
        this.load.image("pixel", require("/src/assets/qci/sprites/pixel.png"));
        this.load.tilemapTiledJSON('map', require("/src/assets/qci/maps/grade_5_landscaping_1.json"));
        this.load.image('help', require('src/assets/qci/sprites/help_icon.png'));
        this.load.atlas("landscape", require("src/assets/qci/atlas/gameLandscape.png"), require("src/assets/qci/atlas/gameLandscape.json"));
        this.load.atlas("star-disk", require("src/assets/qci/atlas/starDisk.png"), require("src/assets/qci/atlas/starDisk.json"));
        this.load.atlas("star-hauler", require("src/assets/qci/atlas/starHauler.png"), require("src/assets/qci/atlas/starHauler.json"));
        this.load.image("rover", require("src/assets/qci/atlas/landscape/star-ship_R.png"));
        this.load.spritesheet('player', require('src/assets/qci/sprites/studentPilot/student_spritemap-42x64.png'), { frameWidth: 42, frameHeight: 64 });
        this.load.image("background", require("/src/assets/qci/backgrounds/planet_r_top.jpg"));

        PhaserGamePad.preload(this);
        var keys = [
            "Landscaping.Phaser.Feedback1",
            "Landscaping.Phaser.Feedback2",
            "Landscaping.Phaser.Feedback3",
        ];

        keys.forEach((key) => {
            this.load.audio(key, AudioTranslations.FindByKeyAndLanguage(key, TranslateApi.currentLanguage.code).url);
        });
    }

    create(): void {
        bindAllOfThis(this, MainScene.prototype);
        this.initialize();
        this.generateGamepad();
        this.generateVehicles();
        this.generatePlayer();
        this.generateScene();
        this.generateObjects();
        this.generateItemDropZones();
        this.updateScoreboard();
        this.generateDeadzones();
        this.speechBubble = this.createBubble(500, 100, TranslateApi.translate(this.translateString + "starHaulerWrong")).setVisible(false);
        initializeInteractiveCursor(this);
    }

    generatePlayer() {
        this.player = this.physics.add.sprite(this.starDisk.getBottomLeft().x - 50, this.starDisk.getTopLeft().y + 50, "player");
        this.player.setOrigin(.5);
        this.player.setDisplaySize(32, 48);
        this.player.setDepth(4);
        this.player.setFrame(4);

        //  Player animations, turning, walking left and walking right.
        this.anims.create({
            key: 'left',
            frames: this.anims.generateFrameNumbers('player', { start: 0, end: 3 }),
            frameRate: 10,
            repeat: -1
        });

        this.anims.create({
            key: 'turn',
            frames: [{ key: 'player', frame: 4 }],
            frameRate: 20
        });

        this.anims.create({
            key: 'right',
            frames: this.anims.generateFrameNumbers('player', { start: 5, end: 8 }),
            frameRate: 10,
            repeat: -1
        });

        this.player.setCollideWorldBounds(true);
    }

    addHelpButton(): void {
        this.helpButton = this.add.image(0, 0, "help");
        this.helpButton.setOrigin(0);
        this.helpButton.setDisplaySize(30, 30);
        this.helpButton.setPosition(this.gameWidth - this.helpButton.displayWidth - 30, 30);
        this.helpButton.setInteractive();
        this.createHelpText();
        this.helpButton.on('pointerdown', () => {
            this.toggleHelpText();
            this.showingHelpText = !this.showingHelpText;
        });
    }

    generateVehicles() {
        this.map.getObjectLayer("Misc").objects.forEach((ob: TiledGameObject) => {
            if (ob.name === "disk") {
                this.starDisk = this.physics.add.sprite(ob.x, ob.y, "star-disk", "star-disc-landed-empty");
                this.starDisk.setDepth(4);
                this.starDisk.setOrigin(.5);
                this.starDisk.setDisplaySize(ob.width, ob.height);
                this.starDisk.setName("starDisk");
            }
            else if (ob.name === "hauler") {
                this.starHauler = this.physics.add.sprite(ob.x, ob.y, "star-hauler", "star-hauler-left");
                this.starHauler.setDepth(4);
                this.starHauler.setOrigin(.5);
                this.starHauler.setDisplaySize(ob.width, ob.height);
            }
        });
    }

    initialize() {
        this.gameWidth = this.textures.game.config.width as number;
        this.gameHeight = this.textures.game.config.height as number;
        this.allItems = [];
        this.allZones = [];
        this.deadZones = [];
        this.map = this.make.tilemap({ key: 'map' });
        this.speed = this.WALKING_SPEED;
        this.xVel = 0;
        this.yVel = 0;
        this.showingHelpText = true;

        //this.scores.push({ id: TranslateApi.translate(this.translateString + "Items.1.id"), display: TranslateApi.translate(this.translateString + "Items.1.display"), score: 3 });
        //this.scores.push({ id: TranslateApi.translate(this.translateString + "Items.2.id"), display: TranslateApi.translate(this.translateString + "Items.2.display"), score: 10 });
        //this.scores.push({ id: TranslateApi.translate(this.translateString + "Items.3.id"), display: TranslateApi.translate(this.translateString + "Items.3.display"), score: 1 });
        //this.scores.push({ id: TranslateApi.translate(this.translateString + "Items.4.id"), display: TranslateApi.translate(this.translateString + "Items.4.display"), score: 1 });
        this.scores.push({ id: "Tree", display: TranslateApi.translate(this.translateString + "Items.1.display"), score: 3 });
        this.scores.push({ id: "Bush", display: TranslateApi.translate(this.translateString + "Items.2.display"), score: 10 });
        this.scores.push({ id: "Fountain", display: TranslateApi.translate(this.translateString + "Items.3.display"), score: 1 });
        this.scores.push({ id: "Satellite", display: TranslateApi.translate(this.translateString + "Items.4.display"), score: 1 });
    }

    generateScene() {
        this.background = this.add.image(0, 0, "background");
        this.background.setOrigin(0);
        this.background.setDepth(-1);
        this.background.setDisplaySize(this.gameWidth, this.gameHeight);

        this.addHelpButton();

        var sceneLayer = this.map.getObjectLayer('Scene');

        var fontStyle1 = { fontSize: 16, color: '#ffffff', align: 'right' };
        var fontStyle2 = { fontSize: 16, color: '#ffffff' };

        // Generate Objects
        sceneLayer.objects.forEach((ob: TiledGameObject) => {
            if (ob.type !== "") {
                console.warn("Unknown Type Detected", ob.type);
                return;
            }
            var tileName: string = ob.name.toLowerCase();

            if (tileName === "buildings") {
                let garage = this.add.image(ob.x, ob.y, "landscape", "buildings");
                garage.setOrigin(0);
                garage.setDepth(2);
                garage.setDisplaySize(ob.width, ob.height);
            } else if (tileName === "beams") {
                let beams = this.add.image(ob.x, ob.y, "landscape", "beams");
                beams.setOrigin(0);
                beams.setDepth(0);
                beams.setDisplaySize(ob.width, ob.height);
            } else if (tileName === "crane") {
                let crane = this.add.image(ob.x, ob.y, "landscape", "crane");
                crane.setOrigin(0);
                crane.setDepth(0);
                crane.setAngle(15);
                crane.setScale(0.35);
            } else if (tileName === "scoreboard") {
                let scoreboard = this.add.image(ob.x, ob.y, "pixel");
                scoreboard.setOrigin(0);
                scoreboard.setDepth(0);
                scoreboard.setTint(Color.KUDER_SECONDARY);
                scoreboard.setDisplaySize(ob.width, ob.height);
                let border = this.add.image(ob.x - 5, ob.y - 5, "pixel");
                border.setOrigin(0);
                border.setDepth(-.5);
                border.setTint(0x000000);
                border.setDisplaySize(scoreboard.displayWidth + 10, scoreboard.displayHeight + 10);
            } else if (tileName === "blueprint") {
                let blueprint = this.add.image(ob.x, ob.y, "landscape", "blueprint");
                blueprint.setOrigin(0);
                blueprint.setDepth(0);
                blueprint.setDisplaySize(ob.width + 10, ob.height + 10);
            } else if (tileName === "score_1") {
                let txt = this.add.text(ob.x, ob.y, "", fontStyle1);
                txt.setName(tileName);
                txt.setOrigin(0);
                txt.setFontFamily('Russo One, Roboto Mono, Arial');
                txt.setDepth(1);
                txt.setStroke('#ffffff', .5);
                this.scoreTiles.push(txt);
            } else if (tileName === "score_2") {
                let txt = this.add.text(ob.x, ob.y, "", fontStyle2);
                txt.setName(tileName);
                txt.setOrigin(0);
                txt.setDepth(1);
                txt.setFontFamily('Russo One, Roboto Mono, Arial');
                this.scoreTiles.push(txt);
            }
        });
    }

    generateDeadzones(): void {
        this.map.getObjectLayer("Deadzones").objects.forEach((ob: TiledGameObject) => {
            var zone = this.add.zone(ob.x, ob.y, ob.width, ob.height);
            zone.name = ob.name;
            zone.setOrigin(0);
            this.physics.add.existing(zone, true);
            this.physics.add.collider(this.player, zone, null, this.checkCollision);

            var deadZone = new Deadzone(ob.name, ob.properties["canHover"], ob.properties["canDrive"]);
            deadZone.zone = zone;
            this.deadZones.push(deadZone);
        });
    }

    checkCollision(player, zone: Phaser.GameObjects.Zone): boolean {
        var deadZone = this.deadZones.find((dz: Deadzone) => dz.name === zone.name);
        if (this.drivingStarDisk) {
            if (deadZone.canHover) {
                return false;
            }
        }

        if (this.drivingStarHauler) {
            if (deadZone.canDrive) {
                return false;
            }
        }

        // Can walk on bushes deadzone
        if (!this.drivingStarDisk && !this.drivingStarHauler && deadZone.name === "bushes") {
            return false;
        }

        return true;
    }

    inDeadZone(): Deadzone {
        var deadZone: Deadzone;
        for (deadZone of this.deadZones) {
            var areaToCheck = this.drivingStarDisk ? this.starDisk.getBounds() : this.starHauler.getBounds();
            if (Phaser.Geom.Intersects.RectangleToRectangle(areaToCheck, deadZone.zone.getBounds())) {
                return deadZone;
            }
        }
        return null;
    }

    generateObjects() {
        var ob = this.map.getObjectLayer("Misc").objects.find((ob) => {
            return ob.name === "itemBackground";
        }) as TiledGameObject;

        var itemsBackground = this.add.image(ob.x, ob.y, "pixel");
        itemsBackground.setOrigin(0);
        itemsBackground.setDisplaySize(ob.width, ob.height);
        itemsBackground.setTint(Color.KUDER_SECONDARY);
        let border = this.add.image(ob.x - 5, ob.y - 5, "pixel");
        border.setOrigin(0);
        border.setDepth(-.5);
        border.setTint(0x000000);
        border.setDisplaySize(itemsBackground.displayWidth + 10, itemsBackground.displayHeight + 10);

        let tree: string = TranslateApi.translate(this.translateString + "Items.1.id");
        let bush: string = TranslateApi.translate(this.translateString + "Items.2.id");
        let fountain: string = TranslateApi.translate(this.translateString + "Items.3.id");
        let satellite: string = TranslateApi.translate(this.translateString + "Items.4.id");
        var items = [
            {
                name: "Tree", // Used in the scoring system
                texture: "landscape",
                frame: "tree",
                count: this.scores.find(x => x.id === "Tree").score
            },
            {
                name: "Bush", // Used in the scoring system
                texture: "landscape",
                frame: "shrub",
                count: this.scores.find(x => x.id === "Bush").score
            },
            {
                name: "Fountain", // Used in the scoring system
                texture: "landscape",
                frame: "fountain",
                count: this.scores.find(x => x.id === "Fountain").score
            },
            {
                name: "Satellite", // Used in the scoring system
                texture: "landscape",
                frame: "satellite",
                count: this.scores.find(x => x.id === "Satellite").score
            }
        ];

        var i = 0;
        var textStyle = { fontFamily: 'Russo One, Arial', padding: 20, color: "#ffffff", fontSize: 20 };
        this.map.getObjectLayer("Misc").objects.forEach((ob: TiledGameObject) => {
            if (ob.type === "PickupZone") {
                var item = items[i];
                var object;
                for (let index = 0; index < item.count; index++) {
                    object = this.add.image(ob.x, ob.y, item.texture, item.frame);
                    object.setOrigin(0);
                    object.setDisplaySize(ob.width, ob.height);
                    object.setName(item.name);
                    let items = new Item(object, item.name, object.getBounds(), false, item.texture, item.frame);
                    this.allItems.push(items);
                }
                i++;
                // Label for item
                var itemName = TranslateApi.translate(this.translateString + "Items." + i + ".id");
                var itemLabel = this.add.text(ob.x, object.getBottomLeft().y - 5, itemName, textStyle);
                itemLabel.setX(object.x + object.displayWidth / 2 - itemLabel.width / 2);
            }
            else if (ob.name === "text") {
                // Label above all items
                var choose = TranslateApi.translate(this.translateString + "itemsLabel");
                var text = this.add.text(ob.x, ob.y, choose, textStyle);
                text.setX(itemsBackground.x + + itemsBackground.displayWidth / 2 - text.width / 2);
            }
        });
    }

    /*
     * Start driving a vehicle
     */
    aDown() {
        if (!this.canGetOutHere()) {
            return;
        }

        // If they were carrying an item, put it back in it's pick up zone
        if (this.carryingItem) {
            this.resetItem();
        }

        // Stop driving star disk
        if (this.drivingStarDisk) {
            this.speed = this.WALKING_SPEED;
            this.drivingStarDisk = false;
            this.player.setX(this.starDisk.getBottomRight().x + 20);
            this.player.setVisible(true);

            this.children.moveDown(this.starDisk);
            this.children.bringToTop(this.player);

            this.player.setSize(32, 55);
            this.player.setOffset(5, 10);
        }
        // Stop driving star hauler
        else if (this.drivingStarHauler) {
            this.speed = this.WALKING_SPEED;
            this.drivingStarHauler = false;
            this.player.setX(this.starHauler.getBottomRight().x + 20);
            this.player.setVisible(true);
            this.children.moveDown(this.starHauler);
            this.children.bringToTop(this.player);

            this.player.setSize(32, 55);
            this.player.setOffset(5, 10);

            // Make sure don't have beam 
            if (this.starHauler.frame.name.startsWith("star-hauler-left")) {
                this.starHauler.setFrame("star-hauler-left");
            }
            else {
                this.starHauler.setFrame("star-hauler-right");
            }
        }
        // Start driving star disk
        else if (Phaser.Geom.Intersects.RectangleToRectangle(this.player.getBounds(), this.starDisk.getBounds()) && !this.drivingStarHauler) {
            this.speed = this.DRIVING_SPEED;

            this.starDisk.setFrame("star-disc-landed-w-pilot");
            this.player.setVisible(false);
            this.drivingStarDisk = true;
            this.drivingStarHauler = false;
            this.children.bringToTop(this.starDisk);

            // Set player bounds to be bounds of star disk
            this.player.setSize(125, 80);
            this.player.setOffset(-40, -30);
        }
        // Start driving star hauler
        else if (Phaser.Geom.Intersects.RectangleToRectangle(this.player.getBounds(), this.starHauler.getBounds()) && !this.drivingStarDisk) {
            this.speed = this.DRIVING_SPEED;
            this.drivingStarHauler = true;
            this.drivingStarDisk = false;
            if (this.starHauler.frame.name === "star-hauler-left") {
                this.starHauler.setFrame("star-hauler-left");
            }
            else {
                this.starHauler.setFrame("star-hauler-right");
            }
            this.player.setVisible(false);
            this.children.bringToTop(this.starHauler);

            // Set player bounds to be bounds of star hauler
            this.player.setSize(165, 90);
            this.player.setOffset(-60, -10);
        }
    }

    canGetOutHere(): boolean {
        // If they are in a deadzone they can't get out
        var deadzone = this.inDeadZone();
        if (deadzone) {
            return false;
        }

        // Don't allow getting out if on top of the other vehicle
        if (this.drivingStarDisk && Phaser.Geom.Intersects.RectangleToRectangle(this.player.getBounds(), this.starHauler.getBounds())) {
            return false;
        }

        if (this.drivingStarHauler && Phaser.Geom.Intersects.RectangleToRectangle(this.player.getBounds(), this.starDisk.getBounds())) {
            return false;
        }

        return true;
    }

    /**
     * Pick up item with vehicle
     */
    bDown() {
        // Drop an item if you're carrying it
        if (this.carryingItem) {
            var zone: Zone;
            for (zone of this.allZones) {
                // Dropped in correct zone
                if (Phaser.Geom.Intersects.RectangleToRectangle(this.currentItem.object.getBounds(), zone.rect) &&
                    this.currentItem.name === zone.name && !zone.placed) {
                    this.currentItem.object.setPosition(zone.object.x, zone.object.y);
                    this.currentItem.object.setDisplaySize(zone.object.displayWidth, zone.object.displayHeight);
                    zone.placed = true;
                    this.scores.find(x => x.id === this.currentItem.object.name).score--;
                    this.updateScoreboard();
                    this.carryingItem = false;
                    this.currentItem.follow = false;
                    this.currentItem.dropped = true;
                    this.currentItem.object.setDepth(-.5);
                    this.currentItem = null;
                    break;
                }
            }
        }
        else {
            // Pick up an item with star disk
            if (this.drivingStarDisk) {
                this.starDisk.setFrame("star-disc-beam");
                var smallItem: Item;
                for (smallItem of this.allItems) {
                    let doesIntersect = Phaser.Geom.Intersects.RectangleToRectangle(this.player.getBounds(), smallItem.rect);
                    if (doesIntersect && (smallItem.name === "Tree" || smallItem.name === "Bush") && !smallItem.dropped) {
                        this.currentItem = smallItem;
                        this.currentItem.name = smallItem.object.name;
                        this.currentItem.object.setDepth(3);
                        if (smallItem.name === "Bush") {
                            // Make bush a little bigger than the size it will be when it's placed
                            this.currentItem.object.setDisplaySize(smallItem.object.displayWidth * .35, smallItem.object.displayHeight * .35);
                        }
                        if (smallItem.name === "Tree") {
                            this.currentItem.object.setDisplaySize(smallItem.object.displayWidth * .5, smallItem.object.displayHeight * .5);
                        }
                        this.carryingItem = true;
                        this.currentItem.follow = true;
                        this.children.bringToTop(this.starDisk);
                        this.currentItem.object.setDepth(this.starHauler.depth + 1);
                        this.fadeOutFeedback();
                        break;
                    }
                    else if (doesIntersect && (smallItem.name !== "Tree" && smallItem.name !== "Bush")) {
                        if (smallItem.name === "Fountain" && this.scores.find(x => x.id === "Fountain").score !== 0) {
                            this.setSpeechBubbleText(TranslateApi.translate(this.translateString + "starDiskWrong"));
                            this.tweenFeedback();
                        }
                        if (smallItem.name === "Satellite" && this.scores.find(x => x.id === "Satellite").score !== 0) {
                            this.setSpeechBubbleText(TranslateApi.translate(this.translateString + "starDiskWrong"));
                            this.tweenFeedback();
                        }
                    }
                }
            }
            // Pick up an item with star hauler
            if (this.drivingStarHauler) {
                // Switch to texture with the beam
                if (this.starHauler.frame.name === "star-hauler-left") {
                    this.starHauler.setFrame("star-hauler-left-beam");
                }
                else if (this.starHauler.frame.name === "star-hauler-right") {
                    this.starHauler.setFrame('star-hauler-right-beam');
                }

                var largeItem: Item;
                for (largeItem of this.allItems) {
                    let doesIntersect = Phaser.Geom.Intersects.RectangleToRectangle(this.player.getBounds(), largeItem.rect);
                    if (doesIntersect && (largeItem.name === "Fountain" || largeItem.name === "Satellite") && !largeItem.dropped) {
                        this.currentItem = largeItem;
                        this.currentItem.name = largeItem.object.name;
                        this.currentItem.object.setDisplaySize(largeItem.object.displayWidth * .6, largeItem.object.displayHeight * .6);
                        this.currentItem.object.setDepth(3);
                        this.carryingItem = true;
                        this.currentItem.follow = true;
                        this.currentItem.object.setDepth(this.starDisk.depth + 1);
                        this.fadeOutFeedback();
                        break;
                    }
                    else if (doesIntersect && (largeItem.name !== "Fountain" && largeItem.name !== "Satellite")) {
                        if (largeItem.name === "Bush" && this.scores.find(x => x.id === "Bush").score !== 0) {
                            this.setSpeechBubbleText(TranslateApi.translate(this.translateString + "starHaulerWrong"));
                            this.tweenFeedback();
                        }
                        if (largeItem.name === "Tree" && this.scores.find(x => x.id === "Tree").score !== 0) {
                            this.setSpeechBubbleText(TranslateApi.translate(this.translateString + "starHaulerWrong"));
                            this.tweenFeedback();
                        }
                    }
                }
            }
        }
    }

    tweenFeedback(): void {
        if (this.tweens.isTweening(this.speechBubble)) {
            this.tweens.killTweensOf(this.speechBubble);
        }

        this.tweens.add({
            targets: this.speechBubble,
            x: this.speechBubble.x,
            duration: 2000,
            ease: "Linear",
            onStart: () => {
                this.speechBubble.setAlpha(1);
                this.speechBubble.setVisible(true);
                this.children.bringToTop(this.speechBubble);
            },
            onComplete: () => {
                this.fadeOutFeedback();
            }
        });
    }

    fadeOutFeedback(): void {
        this.tweens.add({
            targets: this.speechBubble,
            x: this.speechBubble.x,
            duration: 500,
            alpha: {
                getStart: () => 1,
                getEnd: () => 0,
            },
            ease: "Linear",
            onComplete: () => {
                this.speechBubble.setVisible(false);
                this.children.moveDown(this.speechBubble);
            }
        });
    }


    updateScoreboard() {
        var numberTile = this.scoreTiles.find(x => x.name === "score_1");
        var textTile = this.scoreTiles.find(x => x.name === "score_2");

        numberTile.setText([
            this.scores[0].score.toString(),
            "",
            this.scores[1].score.toString(),
            "",
            this.scores[2].score.toString(),
            "",
            this.scores[3].score.toString(),
            ""
        ]);
        textTile.setText([
            this.scores[0].display,
            "",
            this.scores[1].display,
            "",
            this.scores[2].display,
            "",
            this.scores[3].display,
            ""
        ]);

        // Game is done
        if (this.isDone()) {
            setTimeout(() => {
                this.player.disableBody();
                var config = new PhaserModalConfig();
                config.title = TranslateApi.translate(this.translateString + "doneText");
                config.buttonConfig = new PhaserButtonConfig();
                config.buttonConfig.smartSize = true;
                config.forceResponse = true;
                var modal = new PhaserModal(this, 0, 0, [{ id: 1, displayText: TranslateApi.translate("common.done") }], config);
                modal.setDepth(100);
                modal.setPosition(this.gameWidth / 2 - modal.width / 2, this.gameHeight / 2 - modal.height / 2);
                modal.on("optionClicked", () => {
                    this.sound.stopAll();
                    modal.destroy();
                    this.player.disableBody();
                    this.input.setDefaultCursor("default");
                    this.input.removeAllListeners();
                //    this.scene.add("EndGameScene", EndGameScene, true); // rm double scene
                    this.scene.remove(this);
                    this.sound.stopAll();
                    // Game Over
                    (this.sys.game as any).onComplete();
                });

            }, 1000);
        }
    }


    updateObjectVehiclePosition() {
        if (this.drivingStarDisk) {
            if (this.carryingItem) {
                this.starDisk.setFrame("star-disc-beam");
            }
            else {
                this.starDisk.setFrame("star-disc-floating");
            }
            this.starDisk.x = this.player.x;
            this.starDisk.y = this.player.y;
            if (this.currentItem != null && this.currentItem.follow) {
                this.currentItem.object.x = this.player.x - this.currentItem.object.displayWidth / 2;
                if (this.currentItem.name === "Tree") {
                    this.currentItem.object.y = this.starDisk.y + 35;
                }
                else if (this.currentItem.name === "Bush") {
                    this.currentItem.object.y = this.starDisk.y + 30;
                }
            }
        }
        else {
            this.starDisk.setFrame("star-disc-landed-empty");
        }

        if (this.drivingStarHauler) {
            this.starHauler.x = this.player.x;
            this.starHauler.y = this.player.y;
            if (this.currentItem != null && this.currentItem.follow) {
                if (this.starHauler.frame.name === "star-hauler-left-beam") {
                    // Driving left
                    this.currentItem.object.x = this.player.x + 15;
                }
                else if (this.starHauler.frame.name === "star-hauler-right-beam") {
                    // Driving right
                    this.currentItem.object.x = this.player.x - 50;
                }
                // Moving up or down
                this.currentItem.object.y = this.player.y - 45;
            }
        }
    }

    generateItemDropZones() {
        var objectLayer = this.map.getObjectLayer('DropZones');
        objectLayer.objects.forEach((ob: TiledGameObject) => {
            var object = this.add.image(ob.x + ob.width / 2, ob.y + ob.height / 2, "landscape", "hotspot");
            object.setOrigin(0);
            object.setDepth(2);
            object.setName(ob.name);
            object.setVisible(false);
            object.setDisplaySize(ob.width, ob.height);
            var zone = new Zone(object, ob.name, object.getBounds(), false);
            this.allZones.push(zone);
        });
    }

    createHelpText(): void {
        var translateString = "Landscaping.Phaser.Instruction.";
        var borderColor = 0xffffff;

        var textStyle = { fontFamily: 'Russo One, Arial', fontSize: 16, padding: 10 };
        let bpText = TranslateApi.translate(translateString + "bp");
        let bpTextOb = this.add.text(1000, 280, bpText, textStyle).setDepth(3).setWordWrapWidth(300);
        bpTextOb.setBackgroundColor("#000000").setOrigin(0);
        let bpBorder = this.add.image(bpTextOb.x - 5, bpTextOb.y - 5, "pixel").setDepth(1);
        bpBorder.setDisplaySize(bpTextOb.displayWidth + 10, bpTextOb.displayHeight + 10).setTint(borderColor).setOrigin(0);

        let diskText = TranslateApi.translate(translateString + "disk");
        let diskTextOb = this.add.text(this.starDisk.getBottomLeft().x, this.starDisk.getBottomRight().y - 5, diskText, textStyle).setDepth(3).setWordWrapWidth(260);
        diskTextOb.setName('diskTextOb');
        diskTextOb.setBackgroundColor("#000000");
        let diskBorder = this.add.image(this.starDisk.getCenter().x, diskTextOb.y - 5, "pixel").setDepth(1);
        diskBorder.setName("diskBorder");
        diskBorder.setDisplaySize(diskTextOb.displayWidth + 10, diskTextOb.displayHeight + 10).setTint(borderColor);
        Phaser.Display.Align.In.Center(diskBorder, diskTextOb);

        let haulText = TranslateApi.translate(translateString + "haul");
        let haulTextOb = this.add.text(this.starHauler.getBottomLeft().x, this.starHauler.getBottomRight().y + 10, haulText, textStyle).setDepth(3).setWordWrapWidth(300);
        haulTextOb.setName("haulerTextOb");
        haulTextOb.setBackgroundColor("#000000");
        let haulBorder = this.add.image(this.starHauler.getCenter().x, haulTextOb.getCenter().y, "pixel").setDepth(1);
        haulBorder.setName("haulerBorder");
        haulBorder.setDisplaySize(haulTextOb.displayWidth + 10, haulTextOb.displayHeight + 10).setTint(borderColor);
        Phaser.Display.Align.In.Center(haulBorder, haulTextOb);

        let carText = TranslateApi.translate(translateString + "car");
        let carTextOb = this.add.text(500, 40, carText, textStyle).setDepth(3).setWordWrapWidth(250);
        carTextOb.setBackgroundColor("#000000").setOrigin(0);
        let carBorder = this.add.image(carTextOb.x - 5, carTextOb.y - 5, "pixel").setDepth(1);
        carBorder.setDisplaySize(carTextOb.displayWidth + 10, carTextOb.displayHeight + 10).setTint(borderColor).setOrigin(0);

        let houseText = TranslateApi.translate(translateString + "house");
        let houseTextOb = this.add.text(1050, 450, houseText, textStyle).setDepth(3).setWordWrapWidth(215);
        houseTextOb.setBackgroundColor("#000000").setOrigin(0);
        let houseBorder = this.add.image(houseTextOb.x - 5, houseTextOb.y - 5, "pixel").setDepth(1);
        houseBorder.setDisplaySize(houseTextOb.displayWidth + 10, houseTextOb.displayHeight + 10).setTint(borderColor).setOrigin(0);

        this.helpText = [bpBorder, bpTextOb, carBorder, carTextOb, houseBorder, houseTextOb, diskBorder, diskTextOb, haulBorder, haulTextOb];
    }

    toggleHelpText(): void {
        var diskTextOb, diskBorder, haulerTextOb, haulerBorder;
        this.helpText.forEach((ob: PhaserGeneralGameObjectType) => {
            ob.visible = !ob.visible;

            if (ob.name === "diskTextOb") {
                diskTextOb = ob;
            }
            else if (ob.name === "diskBorder") {
                diskBorder = ob;
            }
            else if (ob.name === "haulerTextOb") {
                haulerTextOb = ob;
            }
            else if (ob.name === "haulerBorder") {
                haulerBorder = ob;
            }

            if (ob.visible) {
                this.children.bringToTop(ob);
            }
            else {
                this.children.moveDown(ob);
            }
        });

        // Don't let the star disk and star hauler labels run off the screen
        var x, y;
        x = Math.max(75, Math.min(this.starDisk.getCenter().x, this.gameWidth - this.starDisk.displayWidth));
        y = Math.min(this.starDisk.y + this.starDisk.displayHeight / 2 + 5, this.gameHeight - this.starDisk.displayHeight);

        diskBorder.setPosition(x, y);
        Phaser.Display.Align.In.Center(diskTextOb, diskBorder);

        x = Math.max(75, Math.min(this.starHauler.getCenter().x - 5, this.gameWidth - this.starHauler.displayWidth));
        y = Math.min(this.starHauler.y + this.starHauler.displayHeight, this.gameHeight - this.starHauler.displayHeight - 10);

        haulerBorder.setPosition(x, y);
        Phaser.Display.Align.In.Center(haulerTextOb, haulerBorder);
    }

    createBubble(x: number, y: number, text: string): Phaser.GameObjects.Container {
        var padding = 10;
        var fontSize = 14;
        var borderWidth = 5;
        var borderColor = Color.PLANET_COLORS.get("R");
        var textBox = this.add.container(x, y);
        var textStyle = { fontFamily: 'Russo One, Arial', color: "#ffffff", fontSize: fontSize, padding: 10 };

        var textObject = this.add.text(padding + borderWidth, padding + borderWidth, text, textStyle);
        textObject.setWordWrapWidth(195 + padding, true);
        textObject.setName("Bubble");
        textBox.setSize(textObject.displayWidth + padding + borderWidth, textObject.displayHeight + padding + borderWidth);

        // Add text box background
        var textBoxBackground = this.add.image(0, 0, "pixel");
        textBoxBackground.setOrigin(0);
        textBoxBackground.setDisplaySize(textBox.displayWidth, textBox.displayHeight);
        textBoxBackground.setTint(0x000000);
        textBoxBackground.setPosition(borderWidth + padding + textStyle.padding, borderWidth + padding + textStyle.padding);

        // Add text box border
        var textBoxBorder = this.add.image(0, 0, "pixel");
        textBoxBorder.setOrigin(0);
        textBoxBorder.setDisplaySize(textBoxBackground.displayWidth + 2 * borderWidth, textBoxBackground.displayHeight + 2 * borderWidth);
        textBoxBorder.setTint(borderColor);
        textBoxBackground.setPosition(borderWidth, borderWidth);

        textBox.add(textBoxBorder);
        textBox.add(textBoxBackground);
        textBox.add(textObject);

        return textBox;
    }

    resetItem(): void {
        this.currentItem.object.setDisplaySize(64, 64);
        this.currentItem.object.setPosition(this.currentItem.startPosition.x, this.currentItem.startPosition.y);
        this.currentItem.object.setDepth(this.starDisk.depth - 1);
        this.currentItem = null;
        this.carryingItem = false;

        // Get rid of beam
        if (this.drivingStarHauler) {
            if (this.starHauler.frame.name === "star-hauler-left-beam") {
                this.starHauler.setFrame("star-hauler-left");
            }
            else if (this.starHauler.frame.name === "star-hauler-right-beam") {
                this.starHauler.setFrame("star-hauler-right");
            }
        }
    }

    // Checks if all items have been dropped in their respective dragzones
    isDone(): boolean {
        for (var i = 0; i < this.scores.length; i++) {
            if (this.scores[i].score > 0) {
                return false;
            }
        }
        return true;
    }

    // Show speech bubble
    setSpeechBubbleText(text: string) {
        this.speechBubble.setVisible(true);
        var textObject = this.speechBubble.list.find((c) => {
            return c.name === "Bubble";
        }) as Phaser.GameObjects.Text;
        textObject.setText(text);
    }

    update(): void {
        this.updatePlayerMovement();
        this.updateObjectVehiclePosition();
    }

    /**
     * GamePad/Player Movement
     */
    generateGamepad(): void {
        this.gamePad = new PhaserGamePad(this);
        this.gamePad.on("keydown_a", this.aDown);
        this.gamePad.on("keydown_b", this.bDown);
    }

    updatePlayerMovement(): void {
        if (!this.player.body.enable) {
            return;
        }

        this.xVel = 0;
        this.yVel = 0;

        if (this.gamePad.keys.left.isDown) {
            this.xVel += -this.speed;
        }
        if (this.gamePad.keys.right.isDown) {
            this.xVel += this.speed;
        }
        if (this.gamePad.keys.down.isDown) {
            this.yVel += this.speed;
        }
        if (this.gamePad.keys.up.isDown) {
            this.yVel += -this.speed;
        }

        this.player.setVelocity(this.xVel, this.yVel);
        
        // If showing help text and moving, hide the help text
        if(this.showingHelpText && (this.xVel !== 0 || this.yVel !== 0)) {
            this.showingHelpText = false;
            this.toggleHelpText();
        }

        // Play walking animations
        if (this.xVel > 0) {
            this.player.anims.play('right', true);
        }
        else if (this.xVel < 0) {
            this.player.anims.play('left', true);
        }
        else {
            this.player.anims.play('turn');
        }


        if (this.drivingStarHauler) {
            this.updateStarHauler();
        }
    }

    updateStarHauler(): void {
        if (this.drivingStarHauler && this.player.body.velocity.x < 0) {
            // Going left
            if (!this.carryingItem) {
                this.starHauler.setFrame("star-hauler-left");
            }
            else {
                this.starHauler.setFrame("star-hauler-left-beam");
            }
        }
        else if (this.drivingStarHauler && this.player.body.velocity.x > 0) {
            // Going right
            if (!this.carryingItem) {
                this.starHauler.setFrame("star-hauler-right");
            }
            else {
                this.starHauler.setFrame("star-hauler-right-beam");
            }
        }
    }

}



// WEBPACK FOOTER //
// ./src/games/LandscapingGame/scenes/MainScene.ts